import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast, Toaster } from 'sonner';
import { 
  Building, MapPin, Home, Phone, Mail, Calendar, Check, 
  ChevronRight, Play, Image, FileText, Shield, Dumbbell, 
  TreePine, Waves, Car, Clock, Heart, Share2, Download,
  Bed, Bath, Square, Compass, ArrowRight, X
} from 'lucide-react';
import ProgressiveImage from '@/components/ProgressiveImage';

const API_URL = '';

const ProjectLanding = () => {
  const { projectId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const [showEnquiryModal, setShowEnquiryModal] = useState(false);
  const [enquiryForm, setEnquiryForm] = useState({
    name: '', email: '', phone: '', message: ''
  });

  useEffect(() => {
    if (projectId) {
      fetchProjectData();
    }
  }, [projectId]);

  const fetchProjectData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/developer/public/project/${projectId}/full`);
      if (response.status === 403) {
        throw new Error('This project is not yet available for public viewing.');
      }
      if (!response.ok) {
        throw new Error('Project not found');
      }
      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEnquiry = async (e) => {
    e.preventDefault();
    // In a real implementation, this would submit to a lead capture API
    toast.success('Thank you for your interest! Our team will contact you shortly.');
    setShowEnquiryModal(false);
    setEnquiryForm({ name: '', email: '', phone: '', message: '' });
  };

  const formatPrice = (price) => {
    if (price >= 10000000) {
      return `${(price / 10000000).toFixed(2)} Cr`;
    }
    return `${(price / 100000).toFixed(2)} L`;
  };

  // Amenity icon mapping
  const getAmenityIcon = (name) => {
    const iconMap = {
      'gym': Dumbbell,
      'swimming_pool': Waves,
      'pool': Waves,
      'garden': TreePine,
      'parking': Car,
      'security': Shield,
      'clubhouse': Building,
    };
    return iconMap[name?.toLowerCase()] || Home;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-slate-900 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-slate-600">Loading project details...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center max-w-md mx-auto px-4">
          <Building className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Project Not Found</h1>
          <p className="text-slate-500 mb-6">
            The project you're looking for doesn't exist or has been removed.
          </p>
          <Link to="/">
            <Button className="rounded-xl">Go to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const { project, developer, towers, unit_types, inventory, price_range, amenities, media, floor_plans } = data;
  const featuredImages = media?.featured || [];
  const galleryImages = media?.gallery || [];

  return (
    <div className="min-h-screen bg-white" data-testid="project-landing">
      <Toaster position="top-right" richColors />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="absolute inset-0 bg-black/30" />
        
        {/* Hero Content */}
        <div className="relative max-w-7xl mx-auto px-4 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              {/* Developer Badge */}
              {developer && (
                <Badge className="bg-white/20 text-white mb-4 backdrop-blur">
                  By {developer.company}
                </Badge>
              )}
              
              <h1 className="text-4xl lg:text-5xl font-bold mb-4">
                {project?.name}
              </h1>
              
              <div className="flex items-center gap-2 text-white/80 mb-6">
                <MapPin className="w-5 h-5" />
                <span>{project?.location}, {project?.city}</span>
              </div>
              
              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-4 mb-8">
                <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold">
                    {towers?.length || 0}
                  </p>
                  <p className="text-sm text-white/70">Towers</p>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold">
                    {inventory?.total || 0}
                  </p>
                  <p className="text-sm text-white/70">Units</p>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold">
                    {inventory?.available || 0}
                  </p>
                  <p className="text-sm text-white/70">Available</p>
                </div>
              </div>
              
              {/* Price Range */}
              {price_range?.max > 0 && (
                <div className="mb-8">
                  <p className="text-white/70 text-sm mb-1">Starting from</p>
                  <p className="text-3xl font-bold">
                    ₹{formatPrice(price_range.min)}
                    {price_range.max > price_range.min && (
                      <span className="text-lg font-normal text-white/70">
                        {' '}- ₹{formatPrice(price_range.max)}
                      </span>
                    )}
                  </p>
                </div>
              )}
              
              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4">
                <Button 
                  size="lg" 
                  className="rounded-xl bg-white text-slate-900 hover:bg-slate-100"
                  onClick={() => setShowEnquiryModal(true)}
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Enquire Now
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="rounded-xl border-white text-white hover:bg-white/10"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Schedule Visit
                </Button>
              </div>
            </div>
            
            {/* Hero Image/Gallery */}
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-slate-800">
                {galleryImages.length > 0 ? (
                  <ProgressiveImage 
                    src={galleryImages[activeImage]?.url || galleryImages[0]?.url}
                    alt={project?.name}
                    className="w-full h-full"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Building className="w-24 h-24 text-slate-600" />
                  </div>
                )}
              </div>
              
              {/* Image thumbnails */}
              {galleryImages.length > 1 && (
                <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                  {galleryImages.slice(0, 5).map((img, idx) => (
                    <button
                      key={img.media_id || idx}
                      onClick={() => setActiveImage(idx)}
                      className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                        activeImage === idx ? 'border-white' : 'border-transparent opacity-60 hover:opacity-100'
                      }`}
                    >
                      <ProgressiveImage src={img.url} alt="" className="w-full h-full" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Unit Types Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-800 mb-2">Available Configurations</h2>
            <p className="text-slate-500">Choose from a variety of thoughtfully designed floor plans</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {unit_types?.map((unitType) => (
              <div 
                key={unitType._id}
                className="bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center justify-between mb-4">
                  <Badge className="bg-slate-100 text-slate-700 text-lg px-3 py-1">
                    {unitType._id}
                  </Badge>
                  <span className={`text-sm font-medium ${
                    unitType.available > 0 ? 'text-emerald-600' : 'text-red-500'
                  }`}>
                    {unitType.available > 0 ? `${unitType.available} Available` : 'Sold Out'}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-4 text-sm text-slate-600">
                  <div className="flex items-center gap-2">
                    <Square className="w-4 h-4" />
                    <span>{unitType.min_area?.toFixed(0)} - {unitType.max_area?.toFixed(0)} sqft</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Home className="w-4 h-4" />
                    <span>{unitType.total} Total Units</span>
                  </div>
                </div>
                
                <div className="border-t border-slate-100 pt-4">
                  <p className="text-sm text-slate-500 mb-1">Price Range</p>
                  <p className="text-xl font-bold text-slate-800">
                    ₹{formatPrice(unitType.min_price)} - ₹{formatPrice(unitType.max_price)}
                  </p>
                </div>
                
                <Button 
                  className="w-full mt-4 rounded-xl"
                  variant={unitType.available > 0 ? 'default' : 'outline'}
                  onClick={() => setShowEnquiryModal(true)}
                  disabled={unitType.available === 0}
                >
                  {unitType.available > 0 ? 'Enquire Now' : 'Join Waitlist'}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Floor Plans Section */}
      {floor_plans?.length > 0 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-800 mb-2">Floor Plans</h2>
              <p className="text-slate-500">Detailed layouts for every unit type</p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {floor_plans.map((plan) => (
                <div 
                  key={plan.plan_id}
                  className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow"
                >
                  <div className="h-48 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                    <FileText className="w-16 h-16 text-slate-400" />
                  </div>
                  <div className="p-6">
                    <h3 className="font-semibold text-lg text-slate-800 mb-2">{plan.name}</h3>
                    <Badge variant="outline" className="mb-3">{plan.unit_type}</Badge>
                    
                    <div className="grid grid-cols-3 gap-2 text-sm text-slate-600 mb-4">
                      <div className="flex items-center gap-1">
                        <Bed className="w-4 h-4" />
                        <span>{plan.bedrooms}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Bath className="w-4 h-4" />
                        <span>{plan.bathrooms}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Square className="w-4 h-4" />
                        <span>{plan.carpet_area}</span>
                      </div>
                    </div>
                    
                    <p className="text-lg font-semibold text-slate-800">
                      Starting ₹{formatPrice(plan.base_price)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Amenities Section */}
      {(amenities?.clubhouses?.length > 0 || amenities?.outdoor_spaces?.length > 0) && (
        <section className="py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-800 mb-2">World-Class Amenities</h2>
              <p className="text-slate-500">Experience luxury living with premium facilities</p>
            </div>
            
            {/* Clubhouses */}
            {amenities?.clubhouses?.length > 0 && (
              <div className="mb-12">
                <h3 className="text-xl font-semibold text-slate-800 mb-6">Clubhouse Facilities</h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {amenities.clubhouses.map((club) => (
                    <div key={club.clubhouse_id} className="bg-white rounded-2xl p-6 border border-slate-200">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                          <Building className="w-6 h-6 text-slate-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-800">{club.name}</h4>
                          <p className="text-sm text-slate-500">{club.total_area_sqft?.toLocaleString()} sqft</p>
                        </div>
                      </div>
                      
                      {club.amenities?.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {club.amenities.map((amenity, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {amenity.name || amenity}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Outdoor Spaces */}
            {amenities?.outdoor_spaces?.length > 0 && (
              <div>
                <h3 className="text-xl font-semibold text-slate-800 mb-6">Outdoor & Recreation</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {amenities.outdoor_spaces.map((space) => {
                    const IconComponent = getAmenityIcon(space.space_type);
                    return (
                      <div 
                        key={space.space_id}
                        className="bg-white rounded-xl p-4 text-center border border-slate-200 hover:shadow-md transition-shadow"
                      >
                        <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                          <IconComponent className="w-6 h-6 text-slate-600" />
                        </div>
                        <p className="text-sm font-medium text-slate-700">{space.name}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Towers Section */}
      {towers?.length > 0 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-800 mb-2">Project Structure</h2>
              <p className="text-slate-500">{towers.length} towers with {inventory?.total} units</p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {towers.map((tower) => (
                <div 
                  key={tower.tower_id}
                  className="bg-white rounded-2xl border border-slate-200 p-6 text-center hover:shadow-lg transition-shadow"
                >
                  <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Building className="w-8 h-8 text-slate-600" />
                  </div>
                  <h3 className="font-semibold text-lg text-slate-800 mb-2">{tower.name}</h3>
                  <div className="flex justify-center gap-4 text-sm text-slate-500">
                    <span>{tower.total_floors || 0} Floors</span>
                    <span>{tower.total_units || 0} Units</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Project Details */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold mb-6">About {project?.name}</h2>
              <p className="text-slate-300 mb-6 leading-relaxed">
                {project?.description || `Experience modern living at ${project?.name}, a premium residential project located in the heart of ${project?.city}. With thoughtfully designed spaces and world-class amenities, this project offers the perfect blend of comfort and convenience.`}
              </p>
              
              {/* Key Features */}
              <div className="space-y-3">
                {project?.rera_number && (
                  <div className="flex items-center gap-3 text-slate-300">
                    <Check className="w-5 h-5 text-emerald-400" />
                    <span>RERA Approved: {project.rera_number}</span>
                  </div>
                )}
                <div className="flex items-center gap-3 text-slate-300">
                  <Check className="w-5 h-5 text-emerald-400" />
                  <span>Premium Location in {project?.city}</span>
                </div>
                <div className="flex items-center gap-3 text-slate-300">
                  <Check className="w-5 h-5 text-emerald-400" />
                  <span>{inventory?.sold_percentage?.toFixed(0)}% Sold</span>
                </div>
              </div>
            </div>
            
            {/* Contact Card */}
            <div className="bg-white rounded-2xl p-8 text-slate-800">
              <h3 className="text-xl font-semibold mb-6">Get in Touch</h3>
              
              {developer && (
                <div className="mb-6 pb-6 border-b border-slate-200">
                  <p className="text-sm text-slate-500 mb-1">Developer</p>
                  <p className="font-semibold text-lg">{developer.company}</p>
                  {developer.rera_number && (
                    <p className="text-sm text-slate-500">RERA: {developer.rera_number}</p>
                  )}
                </div>
              )}
              
              <Button 
                size="lg" 
                className="w-full rounded-xl mb-4"
                onClick={() => setShowEnquiryModal(true)}
              >
                <Phone className="w-4 h-4 mr-2" />
                Request Callback
              </Button>
              
              <Button 
                size="lg" 
                variant="outline"
                className="w-full rounded-xl"
                onClick={() => setShowEnquiryModal(true)}
              >
                <Calendar className="w-4 h-4 mr-2" />
                Schedule Site Visit
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky CTA Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 lg:hidden z-40">
        <div className="flex gap-4 max-w-lg mx-auto">
          <Button 
            className="flex-1 rounded-xl"
            onClick={() => setShowEnquiryModal(true)}
          >
            <Phone className="w-4 h-4 mr-2" />
            Call Now
          </Button>
          <Button 
            variant="outline"
            className="flex-1 rounded-xl"
            onClick={() => setShowEnquiryModal(true)}
          >
            Enquire
          </Button>
        </div>
      </div>

      {/* Enquiry Modal */}
      {showEnquiryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-semibold text-xl">Enquire About {project?.name}</h3>
              <button onClick={() => setShowEnquiryModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleEnquiry} className="space-y-4">
              <div>
                <Label>Full Name *</Label>
                <Input
                  className="mt-1 rounded-xl"
                  value={enquiryForm.name}
                  onChange={(e) => setEnquiryForm({ ...enquiryForm, name: e.target.value })}
                  required
                />
              </div>
              
              <div>
                <Label>Phone Number *</Label>
                <Input
                  type="tel"
                  className="mt-1 rounded-xl"
                  value={enquiryForm.phone}
                  onChange={(e) => setEnquiryForm({ ...enquiryForm, phone: e.target.value })}
                  required
                />
              </div>
              
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  className="mt-1 rounded-xl"
                  value={enquiryForm.email}
                  onChange={(e) => setEnquiryForm({ ...enquiryForm, email: e.target.value })}
                />
              </div>
              
              <div>
                <Label>Message</Label>
                <textarea
                  className="mt-1 w-full rounded-xl border border-slate-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                  rows={3}
                  value={enquiryForm.message}
                  onChange={(e) => setEnquiryForm({ ...enquiryForm, message: e.target.value })}
                  placeholder="I'm interested in this project..."
                />
              </div>
              
              <Button type="submit" className="w-full rounded-xl">
                Submit Enquiry
              </Button>
              
              <p className="text-xs text-slate-500 text-center">
                By submitting, you agree to our privacy policy and terms of service.
              </p>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectLanding;
